import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { passwordError } from "@/lib/password";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrentPassword(""); setNewPassword(""); setConfirm("");
    setShowCurrent(false); setShowNew(false);
  };

  const handleOpenChange = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (passwordError(newPassword)) { toast.error(passwordError(newPassword)); return; }
    if (newPassword !== confirm) { toast.error("A confirmação não bate com a nova senha"); return; }
    if (newPassword === currentPassword) { toast.error("A nova senha deve ser diferente da atual"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Senha alterada com sucesso");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display uppercase flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-green-brand" /> Alterar senha
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Digite sua senha atual, a nova senha e confirme para gravar.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
          <div>
            <Label className="text-xs font-mono-caps text-zinc-400">Senha atual</Label>
            <div className="relative mt-1">
              <Input
                data-testid="current-password-input"
                required
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="pr-10 bg-zinc-900 border-zinc-800"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                data-testid="toggle-current-password"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
                aria-label={showCurrent ? "Ocultar senha" : "Mostrar senha"}
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono-caps text-zinc-400">Nova senha</Label>
            <div className="relative mt-1">
              <Input
                data-testid="new-password-input"
                required
                minLength={8}
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pr-10 bg-zinc-900 border-zinc-800"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                data-testid="toggle-new-password"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
                aria-label={showNew ? "Ocultar senha" : "Mostrar senha"}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono-caps text-zinc-400">Confirmar nova senha</Label>
            <Input
              data-testid="confirm-password-input"
              required
              type={showNew ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 bg-zinc-900 border-zinc-800"
              autoComplete="new-password"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" disabled={saving} onClick={() => handleOpenChange(false)} className="text-zinc-300 hover:bg-zinc-900">
              Cancelar
            </Button>
            <Button
              data-testid="change-password-submit"
              type="submit"
              disabled={saving}
              className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Alterar senha"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
